import type { components, paths } from '@connected/api-client/api.generated';
import type { ColumnDef } from '@tanstack/react-table';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { Button } from '@connected/ui/components/button';
import { Carousel, CarouselContent, CarouselItem } from '@connected/ui/components/carousel';
import { File as FileComponent, FileInput, Previewer } from '@connected/ui/components/file';
import { Popout, PopoutBody, PopoutContent, PopoutDescription, PopoutFooter, PopoutHeader, PopoutTitle } from '@connected/ui/components/popout';
import { Skeleton } from '@connected/ui/components/skeleton';
import { useQueries, useQuery } from '@tanstack/react-query';
import JSZip from 'jszip';
import { ChevronDown, ChevronRight, CloudDownload, DownloadCloud, PlusIcon, X, XIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import z from 'zod';

import { DataTable } from './data-table';
import { ExpandedRowsMobile } from './expanded-row';
import { LoadingPage } from './loading-page';

type ApiPath = keyof paths;

type ListAllowedModules = 'electrical';

type CreateNewLog = components['schemas']['CreateElectricalProjectObservationLogPortalCommand'];

type ObservationLog = {
    ObservationLogId: string;
    OldStatus?: string | null;
    NewStatus?: string | null;
    Message?: string | null;
    CreateDate?: string;
    User?: {
        UserId?: string | null;
        Name?: string | null;
        Email?: string | null;
        OrganizationName?: string | null;
    } | null;
    Files?: {
        ObservationLogFileId: string;
        FileId?: string | null;
        LastUpdateDate?: string | null;
        IsDeleted?: boolean | null;
    }[];
};

type ModuleDefinition = {
    ProjectId: 'ElectricalProjectId' | string;
    ProjectIdLower?: string;
    ProjectObservationId: string;

    ProjectObservationLogId: string;
    ProjectObservationLogFileId: string;
    ProjectFileId: string;

    endpoints: {
        FileValidation: ApiPath;
        FileUpload: ApiPath;
        LogCreate: ApiPath;
        LogList: ApiPath;
        FileDownload: ApiPath;
        FilesList: ApiPath;
        RepairObservation: ApiPath;
    };
};

const Modules = {
    electrical: {
        ProjectId: 'ElectricalProjectId',
        ProjectObservationId: 'ElectricalProjectObservationId',

        ProjectObservationLogId: 'ElectricalProjectObservationLogId',
        ProjectFileId: 'ElectricalProjectFileId',
        ProjectObservationLogFileId: 'ElectricalProjectObservationLogFileId',

        endpoints: {
            FileUpload: '/api/v2/portal/electrical/projects/files/upload/observationlog',
            FileValidation: '/api/v2/portal/electrical/projects/files/upload/observationlog',
            LogCreate: '/api/v2/portal/electrical/projects/observations/logs/create',
            LogList: '/api/v2/portal/electrical/projects/observations/logs/list',
            FileDownload: '/api/v2/portal/electrical/projects/files/download',
            FilesList: '/api/v2/portal/electrical/projects/files/list',
            RepairObservation: '/api/v2/portal/electrical/projects/observations/repair',
        },
    },
} as const satisfies Record<ListAllowedModules, ModuleDefinition>;

export function ObservationLogs({
    projectType,
    observationId,
    projectId,
}: {
    projectType: ListAllowedModules;
    observationId: string;
    projectId: string;
}) {
    const { t } = useTranslation('customer-module-electrical');
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [selectedFiles, setSelectedFiles] = useState<ObservationLog | null>(null);
    const [isFilesOpen, setFilesOpen] = useState<boolean>(false);
    const [isResolved, setIsResolved] = useState<boolean>(false);
    const [expandDescription, setExpandDescription] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const moduleConfig = Modules[projectType];
    const scrollY = useRef(0);

    useEffect(() => {
        const SM_BREAKPOINT = 640;

        const handleResize = () => {
            const isSmOrLarger = window.innerWidth >= SM_BREAKPOINT;

            if (isSmOrLarger && expandedId) {
                setExpandedId(null);
            }
            else if (!isSmOrLarger && expandDescription) {
                setExpandDescription(null);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [expandedId, expandDescription]);

    const statusOptions = useMemo(
        () => [
            { value: 'IssueReported', label: t('module-electrical.overview.observations-table.logs.status.issueReported'), color: '#8C8C8C' },
            { value: 'ToSolveIssue', label: t('module-electrical.overview.observations-table.logs.status.toSolveIssue'), color: '#F4841B' },
            { value: 'WontSolveIssue', label: t('module-electrical.overview.observations-table.logs.status.wontSolveIssue'), color: '#E3070A' },
            { value: 'IssueSolved', label: t('module-electrical.overview.observations-table.logs.status.issueSolved'), color: '#099250' },
        ],
        [t],
    );

    const formStatusOptions = useMemo(
        () => statusOptions.filter(option => option.value !== 'IssueSolved'),
        [statusOptions],
    );

    const logbookData = useQuery(api.queryOptions(
        'get',
        moduleConfig.endpoints.LogList,
        {
            params: {
                query: {
                    [moduleConfig.ProjectId]: projectId,
                    [moduleConfig.ProjectObservationId]: observationId,
                },
            },
        },
    ));

    const logs = useMemo(() => {
        if (logbookData.isFetched && logbookData.isSuccess) {
            const normalizedLogs: ObservationLog[] = logbookData?.data?.Observations[0].Logs?.map((log: any) => ({
                ObservationLogId: log[moduleConfig.ProjectObservationLogId],
                OldStatus: log.OldStatus,
                NewStatus: log.NewStatus,
                Message: log.Message,
                CreateDate: log.CreateDate,
                User: log.User,
                Files: log.Files?.map((f: any) => ({
                    ObservationLogFileId: f[moduleConfig.ProjectObservationLogFileId] ?? '',
                    FileId: f[moduleConfig.ProjectFileId] ?? null,
                    LastUpdateDate: f.LastUpdateDate,
                    IsDeleted: f.IsDeleted,
                })),
            })) ?? [];
            return normalizedLogs;
        }
        return null;
    }, [logbookData?.data?.Observations, logbookData.isFetched, logbookData.isSuccess, moduleConfig.ProjectFileId, moduleConfig.ProjectObservationLogFileId, moduleConfig.ProjectObservationLogId]);

    const columns = useMemo<ColumnDef<ObservationLog>[]>(() => [
        {
            accessorKey: 'Name',
            meta: {
                sticky: false,
                mobileHidden: true,
                width: 10,
            },
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.name')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell variant="bold">
                    <Button
                        variant="ghost"
                        className="hidden sm:inline-block mr-2"
                        onClick={() => setExpandDescription(prev => prev === row?.original?.ObservationLogId ? null : row?.original?.ObservationLogId ?? null)}
                    >
                        {expandDescription === row?.original?.ObservationLogId ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </Button>
                    <div className="flex items-center text-sm font-bold">
                        {row?.original?.User?.Name || '-'}
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'NewStatus',
            meta: {
                sticky: false,
                width: 10,
            },
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.overview.attached-files-table.columns.status')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <div className="flex flex-row">
                    <Button
                        variant="ghost"
                        className="sm:hidden mr-2"
                        onClick={() => setExpandedId(prev => prev === row?.original?.ObservationLogId ? null : row?.original?.ObservationLogId ?? null)}
                    >
                        {expandedId === row?.original?.ObservationLogId ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </Button>
                    <DataTable.Cell variant={row.original.NewStatus ? 'colorLabel' : 'medium'} color={statusOptions.find(option => option.value === row.original.NewStatus)?.color || '#8C8C8C'}>
                        {row?.original?.NewStatus
                            ? statusOptions.find(option => option.value === row.original.NewStatus)?.label || '-'
                            : '-'}
                    </DataTable.Cell>
                </div>
            ),
        },
        {
            accessorKey: 'OrganizationName',
            meta: {
                sticky: false,
                width: 10,
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.report.logbook-table.columns.maintenance-company')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell variant="medium">
                    {row?.original?.User?.OrganizationName || '-'}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'CreateDate',
            meta: {
                sticky: false,
                width: 10,
                mobileHidden: true,
            },
            header: ({ column }) => (
                <DataTable.Header variant="sortReverse" column={column}>
                    {t('module-electrical.report.logbook-table.columns.createDate')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell variant="medium">
                    {row?.original?.CreateDate ? new Date(row.original.CreateDate).toLocaleDateString() : '-'}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Time',
            meta: {
                sticky: false,
                width: 10,
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.report.logbook-table.columns.time')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell variant="medium">
                    {row?.original?.CreateDate ? new Date(row.original.CreateDate).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' }) : '-'}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Message',
            meta: {
                sticky: false,
                width: 10,
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header className="sm:hidden">
                    {t('module-electrical.overview.attached-files-table.columns.description')}
                </DataTable.Header>
            ),
            cell: ({ row }) => {
                const formattedMessage = row?.original?.Message
                    ?.replace(/(?<!\n)\n(?!\n)/g, '\n\n');
                return (
                    <DataTable.Cell variant="medium">
                        <div className="sm:hidden whitespace-pre-line">
                            {formattedMessage}
                        </div>
                    </DataTable.Cell>
                );
            },
        },
        {
            id: 'actionModal',
            meta: {
                sticky: true,
                width: 10,
            },
            header: '',
            cell: ({ row }) => {
                return (
                    <div className="flex gap-1 items-center justify-end ml-auto">
                        {row?.original?.Files && row?.original?.Files?.length > 0
                            && (
                                <p
                                    onClick={() => {
                                        scrollY.current = window.scrollY;
                                        setSelectedFiles(row?.original);
                                        setFilesOpen(true);
                                    }}
                                    className="font-semibold hover:underline cursor-pointer"
                                >
                                    {t('module-electrical.installation.maintenance-inspection-table.header.files')}
                                </p>
                            )}
                    </div>
                );
            },
        },
    ], [expandDescription, expandedId, statusOptions, t]);

    const tableColumns = useMemo(
        () => logbookData.isLoading
            ? columns.map(column => ({ ...column, cell: () => <Skeleton className="h-8 w-full" /> }))
            : columns,
        [columns, logbookData.isLoading],
    );

    const tableData = useMemo<ObservationLog[]>(
        () => {
            if (logbookData.isError) {
                toast.error(logbookData.error?.Message);
                return [];
            }

            return logs?.sort((a, b) => {
                const dateA = a.CreateDate ? new Date(a.CreateDate).getTime() : 0;
                const dateB = b.CreateDate ? new Date(b.CreateDate).getTime() : 0;
                return dateB - dateA;
            }) || [];
        },
        [logbookData.error?.Message, logbookData.isError, logs],
    );

    return (
        <div>
            <div className="w-full">
                <DataTable.TableHead
                    title={t('module-electrical.overview.observations-table.logs.title')}
                    subtitle={t('module-electrical.overview.observations-table.logs.subtitle')}
                    buttons={[
                        {
                            name: t('module-electrical.overview.observations-table.logs.title'),
                            icon: <PlusIcon size={19} />,
                            callback: () => {
                                scrollY.current = window.scrollY;
                                setIsResolved(false);
                                setIsOpen(true);
                            },
                        },
                        {
                            name: t('module-electrical.overview.observations-table.logs.finishLogBook'),
                            backgroundColor: '#099250',
                            textColor: 'white',
                            disabled: logbookData?.data?.Observations[0]?.LogStatus === 'IssueSolved',
                            callback: () => {
                                scrollY.current = window.scrollY;
                                setIsResolved(true);
                                setIsOpen(true);
                            },
                        },
                    ]}
                    search={null}
                />
                <DataTable
                    className="rounded-tl-none rounded-tr-none"
                    columns={tableColumns}
                    data={tableData}
                    pageSize={100}
                    expandedRow={(row) => {
                        if (expandedId && expandedId === row?.original?.ObservationLogId) {
                            return (
                                <ExpandedRowsMobile columns={columns} data={row.original} />
                            );
                        }
                        if (expandDescription && expandDescription === row?.original?.ObservationLogId) {
                            return (
                                <ExpendedDescription description={row?.original?.Message || '-'} />
                            );
                        }
                    }}
                />
            </div>

            {isOpen && <CreateLogModal isResolved={isResolved} isOpen={isOpen} setIsOpen={setIsOpen} projectType={projectType} observationId={observationId} projectId={projectId} status={formStatusOptions} onSuccess={() => logbookData.refetch()} scrollY={scrollY.current} />}
            {isFilesOpen && <FilesModal projectId={projectId} observation={selectedFiles} isOpen={isFilesOpen} setIsOpen={setFilesOpen} projectType="electrical" scrollY={scrollY.current} />}
        </div>
    );
};

function ExpendedDescription({
    description,
}: {
    description: string;
}) {
    const { t } = useTranslation('customer-module-electrical');
    const formattedMessage = description
        ?.replace(/(?<!\n)\n(?!\n)/g, '\n\n');
    return (
        <div className="pl-8 pt-5 pb-5 hidden sm:flex">
            <div className="2xl:w-1/3 w-full sm:w-1/2 whitespace-pre-line">
                <div className="font-semibold">
                    {t('module-electrical.overview.attached-files-table.columns.description')}
                </div>
                {formattedMessage}
            </div>
        </div>
    );
}

export function CreateLogModal({
    projectType,
    observationId,
    projectId,
    isOpen,
    setIsOpen,
    status,
    isResolved,
    onSuccess,
    scrollY,
}: {
    projectType: ListAllowedModules;
    observationId: string;
    projectId: string;
    isResolved?: boolean;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    status: { value: string; label: string }[];
    onSuccess?: () => void;
    scrollY?: number;
}) {
    const { t } = useTranslation('customer-module-electrical');
    const moduleConfig = Modules[projectType];

    useEffect(() => {
        if (isOpen && scrollY !== undefined) {
            window.scrollTo(0, scrollY);
        }
    }, [isOpen, scrollY]);

    const validateFiles = useQuery(api.queryOptions(
        'get',
        moduleConfig.endpoints.FileValidation,
    ));

    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

    const [loading, setIsLoading] = useState<boolean>(false);

    const fileProps = useMemo(() => {
        if (validateFiles?.isFetched && validateFiles?.isSuccess) {
            return validateFiles?.data;
        }
        return null;
    }, [validateFiles?.data, validateFiles?.isFetched, validateFiles?.isSuccess]);

    const handleRemoveFile = (fileToRemove: File) => {
        setSelectedFiles(prev => prev.filter(file =>
            !(file.name === fileToRemove.name && file.size === fileToRemove.size),
        ));
    };

    const DEFAULT_VALUES: CreateNewLog = {
        User: {
            Name: '',
            Email: '',
            OrganizationName: '',
        },
        ElectricalProjectObservationId: observationId,
        Message: '',
        Status: null,
        Files: [],
    };

    const uploadFilesEndpoint = api.useMutation('post', moduleConfig.endpoints.FileUpload);

    const createLogEndpoint = api.useMutation('post', moduleConfig.endpoints.LogCreate);

    const statusObservationEndpoint = api.useMutation('put', moduleConfig.endpoints.RepairObservation);

    const uploadMultipleFiles = async () => {
        if (selectedFiles.length === 0)
            return [];
        const uploadPromises = selectedFiles.map(row =>
            uploadFilesEndpoint.mutateAsync({
                params: {
                    query: { [moduleConfig.ProjectId]: projectId },
                },
                body: { File: '' },
                bodySerializer: () => {
                    const formData = new FormData();
                    formData.append('File', row as Blob);
                    return formData;
                },
            }),
        );

        const results = await Promise.all(uploadPromises);
        return results.map(r => r.ElectricalProjectFileId).filter((id): id is string | null => id !== undefined);
    };

    const form = useAppForm(
        {
            defaultValues: DEFAULT_VALUES,
            onSubmit: async (values) => {
                setIsLoading(true);
                try {
                    let fileIds: (string | null)[] = [];

                    if (selectedFiles.length > 0) {
                        fileIds = await uploadMultipleFiles();
                    }

                    await createLogEndpoint.mutateAsync({
                        body: {
                            [moduleConfig.ProjectObservationId]: observationId,
                            Files: fileIds
                                .filter((id): id is string => id !== null)
                                .map(id => ({ [moduleConfig.ProjectFileId]: id })),
                            User: {
                                Email: values?.value?.User?.Email,
                                Name: values?.value?.User?.Name,
                                OrganizationName: values?.value?.User?.OrganizationName,
                            },
                            Message: values?.value?.Message,
                            Status: isResolved ? 'IssueSolved' : values?.value?.Status,
                        },
                    });

                    await statusObservationEndpoint.mutateAsync({
                        body: {
                            [moduleConfig.ProjectObservationId]: observationId,
                            IsFixed: isResolved || false,
                        },
                    });

                    toast.success(t('module-electrical.overview.observations-table.logs.message.success'));
                    setIsOpen(false);
                    onSuccess?.();
                }
                catch (err: any) {
                    toast.error(err?.Message ?? t('module-electrical.overview.observations-table.logs.message.fail'));
                }
                finally {
                    setIsLoading(false);
                }
            },
        },
    );

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        form.handleSubmit();
    }

    return (
        <Popout onOpenChange={setIsOpen} open={isOpen}>
            <PopoutContent className="max-w-[640px]">
                <PopoutHeader className="sm:justify-start flex flex-col sm:items-start">
                    <PopoutTitle>
                        {isResolved ? t('module-electrical.overview.observations-table.logs.create.finish') : t('module-electrical.overview.observations-table.logs.create.title')}
                    </PopoutTitle>
                    <PopoutDescription>
                        {t('module-electrical.overview.observations-table.logs.create.subtitle')}
                    </PopoutDescription>
                </PopoutHeader>
                <PopoutBody>
                    <form onSubmit={handleSubmit} className="flex gap-2 flex-col ">
                        <form.AppField
                            name="User.Name"
                            validators={{
                                onChange: validators.createElectricalProjectObservationLogPortalCommand.createElectricalProjectObservationLogPortalCommandUser.name,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('module-electrical.overview.observations-table.logs.create.table.Name')} />
                                    <field.Input
                                        placeholder={t('module-electrical.overview.observations-table.logs.create.table.namePlaceholder')}
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        >
                        </form.AppField>
                        <form.AppField
                            name="User.OrganizationName"
                            validators={{
                                onChange: validators.createElectricalProjectObservationLogPortalCommand.createElectricalProjectObservationLogPortalCommandUser.email,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('module-electrical.overview.observations-table.logs.create.table.companyName')} />
                                    <field.Input
                                        placeholder={t('module-electrical.overview.observations-table.logs.create.table.companyNamePlaceholder')}
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        >
                        </form.AppField>
                        <form.AppField
                            name="User.Email"
                            validators={{
                                onChange: validators.createElectricalProjectObservationLogPortalCommand.createElectricalProjectObservationLogPortalCommandUser.email,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('module-electrical.overview.observations-table.logs.create.table.email')} />
                                    <field.Input
                                        placeholder={t('module-electrical.overview.observations-table.logs.create.table.emailPlaceholder')}
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        >
                        </form.AppField>
                        {!isResolved && (
                            <form.AppField
                                name="Status"
                                validators={{
                                    onChange: z.enum(['IssueReported', 'WontSolveIssue', 'ToSolveIssue', 'IssueSolved']),
                                }}
                                children={field => (
                                    <field.Label>
                                        <field.Label.Title label={t('module-electrical.overview.observations-table.logs.create.table.status')} />
                                        <field.Popover closeOnChange={true}>
                                            <field.RadioGroup options={status ?? []}>
                                                <field.Popover.Trigger asChild>
                                                    <field.RadioGroup.Trigger />
                                                </field.Popover.Trigger>
                                                <field.Popover.Content className="h-auto min-h-30 [&>div]:flex-col [&>div]:items-start [&>div]:gap-6" matchTriggerWidth>
                                                    <field.RadioGroup options={status ?? []} />
                                                </field.Popover.Content>
                                            </field.RadioGroup>
                                        </field.Popover>
                                        <field.ValidationError />
                                    </field.Label>
                                )}
                            />
                        )}
                        <form.AppField
                            name="Message"
                            validators={{
                                onChange: validators.createElectricalProjectObservationLogPortalCommand.message,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('module-electrical.overview.observations-table.logs.create.table.message')} />
                                    <field.Textarea
                                        placeholder={t('module-electrical.overview.observations-table.logs.create.table.messagePlaceholder')}
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        >
                        </form.AppField>
                        <form.AppField
                            name="Files"
                            children={field => (
                                <field.Label asDiv>
                                    <field.Label.Title label={t('module-electrical.overview.observations-table.logs.create.table.fileUpload')} />
                                    <div>
                                        <FileInput
                                            multiple
                                            maxFileSize={fileProps?.MaxFileSize}
                                            minFileSize={fileProps?.MinFileSize}
                                            maxFileName={fileProps?.MaxFileNameLength}
                                            onChange={(files) => {
                                                if (files) {
                                                    const newFiles = Array.from(files);
                                                    setSelectedFiles(prev => [...prev, ...newFiles]);
                                                }
                                            }}
                                            accept="image/jpeg,image/png,image/webp,image/bmp,image/svg+xml,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        />
                                        {selectedFiles.length > 0 && (
                                            <div className="mt-4">
                                                <div className="flex flex-col gap-2">
                                                    {selectedFiles.map(file => (
                                                        <div key={`${file.name}-${file.size}`} className="relative">
                                                            <FileComponent file={file} />
                                                            <Button
                                                                variant="ghost"
                                                                onClick={() => handleRemoveFile(file)}
                                                                className="absolute top-2 right-2 p-1 rounded-full"
                                                            >
                                                                <XIcon size={24} />
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        />
                        <div className="w-full flex flex-row gap-2 mt-5">
                            <Button className="w-full" onClick={() => setIsOpen(false)} variant="outline">{t('module-electrical.cancel')}</Button>
                            <Button disabled={loading} type="submit" variant="default" className="w-full">{t('module-electrical.create')}</Button>
                        </div>
                    </form>
                </PopoutBody>
            </PopoutContent>
        </Popout>
    );
};

function FilesModal({
    projectType,
    observation,
    isOpen,
    setIsOpen,
    projectId,
    scrollY,
}: {
    projectType: ListAllowedModules;
    observation?: ObservationLog | null;
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
    projectId: string;
    scrollY?: number;
}) {
    const moduleConfig = Modules[projectType];
    const { t } = useTranslation('customer-module-electrical');

    useEffect(() => {
        if (isOpen && scrollY !== undefined) {
            window.scrollTo(0, scrollY);
        }
    }, [isOpen, scrollY]);

    const fileTypeIcons = import.meta.glob('@/assets/file-types/*.png', { eager: true, import: 'default' });

    const [imagePreview, setImagePreview] = useState<File | null>(null);

    const fileList = useQuery(api.queryOptions(
        'get',
        moduleConfig.endpoints.FilesList,
        {
            params: {
                query: {
                    [moduleConfig.ProjectId]: projectId,
                    Kind: 'ObservationLog',
                },
            },
        },
    ));

    const fileQueries = useQueries({
        queries: (observation?.Files ?? []).map(file => ({
            ...api.queryOptions(
                'get',
                moduleConfig.endpoints.FileDownload,
                {
                    params: {
                        query: {
                            [moduleConfig.ProjectFileId]: file.FileId!,
                            ImageQuality: 'OriginalWebp',
                        },
                    },
                    parseAs: 'blob',
                },
            ),
            enabled: isOpen,
        })),
    });

    const selectedFiles = useMemo(() => {
        if (fileQueries.every(row => row.status === 'success' || row.status === 'error') && fileList.isFetched && fileList.isSuccess) {
            const rows = fileQueries.map((row, index) => ({
                blob: row?.data,
                id: observation?.Files?.[index]?.FileId,
                status: row?.status,
                fileData: fileList?.data?.Files?.find(row => row?.HasFile && row?.[moduleConfig.ProjectFileId] === observation?.Files?.[index]?.FileId),
            }));

            const imageRows = rows.filter(row => row.status === 'success' && row?.fileData?.HasFile && row?.fileData?.File?.ContentType?.startsWith('image'));
            const docRows = rows.filter(row => row.status === 'success' && row?.fileData?.HasFile && !row?.fileData?.File?.ContentType?.startsWith('image'));

            return {
                images: imageRows.map(file => ({
                    ...file,
                    fileObject: file.blob && file.fileData?.File
                        ? new window.File(
                                [file.blob],
                                file.fileData.File.Name || 'image.jpg',
                                { type: file.fileData.File.ContentType || file.blob.type || 'image/jpeg' },
                            )
                        : null,
                    url: file.blob ? URL.createObjectURL(file.blob) : null,
                })),
                docs: docRows.map(file => ({
                    ...file,
                    fileObject: file.blob && file.fileData?.File
                        ? new window.File(
                                [file.blob],
                                file.fileData.File.Name || 'defaultName.txt',
                                { type: file.blob.type || 'application/octet-stream' },
                            )
                        : null,
                })),
            };
        }
        return null;
    }, [fileList?.data?.Files, fileList.isFetched, fileList.isSuccess, fileQueries, observation?.Files, moduleConfig.ProjectFileId]);

    if (selectedFiles === null || fileQueries?.some(row => row.isLoading) || fileList.isLoading) {
        return (
            <Popout open={isOpen} onOpenChange={setIsOpen}>
                <PopoutContent>
                    <PopoutBody>
                        <LoadingPage />
                    </PopoutBody>
                </PopoutContent>
            </Popout>
        );
    }

    return (
        <div>
            <Popout open={isOpen} onOpenChange={setIsOpen}>
                <PopoutContent className="max-w-[640px]">
                    <PopoutHeader className="sm:justify-start flex flex-col sm:items-start">
                        <PopoutTitle className="w-full">
                            <div className="flex flex-row w-full justify-between">
                                {t('module-electrical.overview.observations-table.logs.files.title')}
                                <X className="cursor-pointer" onClick={() => setIsOpen(false)} />
                            </div>
                        </PopoutTitle>
                        <PopoutDescription>{`${t('module-electrical.overview.observations-table.logs.files.uploadedBy')} ${observation?.User?.Name}`}</PopoutDescription>
                    </PopoutHeader>

                    <PopoutBody>
                        <div className="flex flex-col gap-8 w-full">
                            {selectedFiles?.docs && selectedFiles.docs.length > 0 && (
                                <div>
                                    <p className="font-semibold mb-2">{t('module-electrical.overview.observations-table.logs.documents.title')}</p>
                                    <div className="flex flex-col gap-2">
                                        {selectedFiles?.docs.map(file => (
                                            <div key={file.id} className="relative">
                                                <FileComponent
                                                    previewerDisabled={true}
                                                    iconNode={(
                                                        <img
                                                            src={
                                                                (fileTypeIcons[
                                                                    `/src/assets/file-types/${file?.fileData?.File?.Name?.split('.').pop()?.toLowerCase()}.png`
                                                                ] as string)
                                                                || (fileTypeIcons['/src/assets/file-types/unknown.png'] as string)
                                                            }
                                                            className="h-[2.5rem] w-[2.5rem] object-cover rounded-xs me-2"
                                                            alt=""
                                                        />
                                                    )}
                                                    file={file.fileObject!}
                                                />
                                                <a
                                                    title={file?.fileData?.File?.Name ?? 'image'}
                                                    href={file.blob ? URL.createObjectURL(file.blob) : '#'}
                                                    download={file?.fileData?.File?.Name ?? 'image'}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <Button
                                                        variant="ghost"
                                                        className="absolute top-0 right-2 p-1 rounded-full"
                                                    >
                                                        <DownloadCloud size={22} />
                                                    </Button>
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {selectedFiles?.images && selectedFiles.images.length > 0 && (
                                <div>
                                    <p className="font-semibold mb-2">{t('module-electrical.overview.observations-table.logs.documents.pictures')}</p>
                                    <Carousel loop>
                                        <CarouselContent>
                                            {selectedFiles.images.map((file, idx) => (
                                                <CarouselItem className="pl-2 md:pl-4 basis-1/1" key={file.id ?? idx}>
                                                    {file.url && (
                                                        <img
                                                            className="h-52 w-auto object-contain rounded-lg cursor-pointer"
                                                            alt={file.fileData?.File?.Name ?? ''}
                                                            title={file.fileData?.File?.Name ?? ''}
                                                            src={file.url}
                                                            onClick={() => setImagePreview(file.fileObject)}
                                                        />
                                                    )}
                                                </CarouselItem>
                                            ))}
                                        </CarouselContent>
                                    </Carousel>
                                </div>
                            )}
                        </div>
                    </PopoutBody>
                    <PopoutFooter>
                        {!imagePreview && (
                            <div className="flex w-full justify-end mt-5">
                                <Button
                                    className="min-w-full sm:min-w-16 flex gap-2"
                                    onClick={async () => {
                                        const zip = new JSZip();
                                        const allFiles = [
                                            ...(selectedFiles?.docs ?? []),
                                            ...(selectedFiles?.images ?? []),
                                        ];

                                        for (const file of allFiles) {
                                            if (file.blob && file.fileData?.File?.Name) {
                                                zip.file(file.fileData.File.Name, file.blob);
                                            }
                                        }

                                        const zipBlob = await zip.generateAsync({ type: 'blob' });
                                        const url = URL.createObjectURL(zipBlob);
                                        const link = document.createElement('a');
                                        link.href = url;
                                        link.download = `observation-files-${observation?.ObservationLogId ?? 'files'}.zip`;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                        URL.revokeObjectURL(url);
                                    }}
                                    variant="ghost"
                                >
                                    <CloudDownload size={18} />
                                    {t('module-electrical.overview.observations-table.logs.documents.downloadAll')}
                                </Button>
                            </div>
                        )}
                    </PopoutFooter>
                </PopoutContent>
            </Popout>
            {imagePreview && (
                <Popout open={!!imagePreview} onOpenChange={open => !open && setImagePreview(null)}>
                    <PopoutContent className="max-h-screen sm:w-full">
                        <PopoutHeader className="flex w-full flex-row gap-2">
                            <PopoutTitle className="text-sm">{imagePreview.name}</PopoutTitle>
                        </PopoutHeader>
                        <PopoutBody className="h-full w-full">
                            <Previewer file={imagePreview} className="h-full w-full max-h-[70vh] max-w-[80vw] flex justify-center" />
                        </PopoutBody>
                        <PopoutFooter>
                            <div className="sm:flex justify-between flex-row content-between w-full hidden">
                                <Button variant="ghost" onClick={() => setImagePreview(null)}>{t('module-electrical.close')}</Button>
                                <a
                                    href={URL.createObjectURL(imagePreview)}
                                    download={imagePreview.name ?? 'image'}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Button variant="ghost" onClick={() => setImagePreview(null)}>{t('module-electrical.download')}</Button>
                                </a>
                            </div>
                        </PopoutFooter>
                    </PopoutContent>
                </Popout>
            )}
        </div>
    );
}
